import { BottomAction } from '@euler/app/components/BottomAction';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { ItemMediaList } from '@euler/app/flows/task/components/ItemMediaList';
import { useDeliveryCheckReworkRecordMediaController } from '@euler/app/flows/task/pages/delivery-check/functions/useDeliveryCheckReworkRecordMediaController';
import { ReworkRecordContext } from '@euler/app/flows/task/pages/delivery-check/rework-record/Provider';
import type { ReworkRecordNavParams } from '@euler/app/flows/task/pages/delivery-check/rework-record/screens';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Img } from '@euler/components/image/AdvancedImage';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useAlert } from '@euler/functions/ux/useAlert';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ReworkRecordStaged } from '@euler/model/task-detail/ReworkRecord';
import { formatDateTime } from '@euler/utils/formatters';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useContext, useState } from 'react';
import { ScrollView, Text, TextInput, View } from 'react-native';
import { Fill } from 'react-slot-fill';

export const ReworkRecordPage = wrapNavigatorScreen(
  ({
    recordInfo,
    onDone,
    onCancel,
  }: {
    recordInfo: ReworkRecordStaged;
    onDone?: (record: ReworkRecordStaged) => void;
    onCancel?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const { footerSlotName } = useContext(ReworkRecordContext);

    const alert = useAlert();
    const state = useNavigationState(x => x);
    const routeName = state.routeNames[state.index];
    const hideFooter = Boolean(footerSlotName && routeName !== '_record');

    const navigation =
      useNavigation<StackNavigationProp<ReworkRecordNavParams>>();

    const [responsiblePersonId, setResponsiblePersonId] = useState(
      recordInfo.responsiblePersonId,
    );
    const [responsiblePersonName, setResponsiblePersonName] = useState(
      recordInfo.responsiblePersonName,
    );

    const [remarks, setRemarks] = useState(recordInfo.remarks);

    const {
      medias,
      mediasStaged,
      onTakeMedia,
      onRetryMediaUpload,
      onDeleteMedia,
      onPreviewVideo,
    } = useDeliveryCheckReworkRecordMediaController({ recordInfo });

    const onEditResponsiblePerson = usePersistFn(() => {
      navigation.navigate('_selectMember', {
        onSelect: member => {
          setResponsiblePersonId(member.id);
          setResponsiblePersonName(member.name);
          navigation.goBack();
        },
      });
    });

    const onOk = usePersistFn(() => {
      if (!responsiblePersonId || !responsiblePersonName) {
        void alert({ title: '请选择责任人员!' });
        return;
      }
      if (mediasStaged.length === 0) {
        void alert({ title: '请上传相关图片或视频!' });
        return;
      }
      const record: ReworkRecordStaged = {
        ...recordInfo,
        responsiblePersonId,
        responsiblePersonName,
        medias: mediasStaged,
        remarks,
      };
      onDone?.(record);
    });

    const renderBottomAction = () => {
      if (hideFooter) return null;
      return (
        <BottomAction>
          <BottomAction.Button
            text="取消"
            variant={'default'}
            onPress={onCancel}
          />
          <BottomAction.Button
            text="确认"
            variant={'primary'}
            onPress={onOk}
            css={`
              margin-left: ${rpx(15)}px;
            `}
          />
        </BottomAction>
      );
    };

    return (
      <>
        <ScrollView contentContainerStyle={{ padding: rpx(15) }}>
          <StackLayout
            gap={rpx(10)}
            divider={
              <View
                css={`
                  height: 1px;
                  background-color: rgb(237, 237, 237);
                `}
              />
            }
          >
            {/** top */}
            <StackLayout direction="row" alignItems="center" gap={rpx(15)}>
              <Img
                uri={recordInfo.vehicleImageUrl}
                width={rpx(75)}
                height={rpx(50)}
                source={
                  recordInfo.vehicleImageUrl
                    ? undefined
                    : require('@euler/assets/img/default-car-img.png')
                }
              />
              <StackLayout flex={1} gap={rpx(5)}>
                <StackLayout direction="row" alignItems="center" gap={rpx(8)}>
                  <Typography.Text>{recordInfo.licensePlateNo}</Typography.Text>
                  <Center
                    css={`
                      border-radius: ${rpx(2)}px;
                      border-width: ${rpx(1)}px;
                      border-color: rgb(32, 127, 231);
                      padding: ${rpx(0)}px ${rpx(5)}px;
                    `}
                  >
                    <Typography.Text color={'rgb(32, 127, 231)'} size={rpx(12)}>
                      {recordInfo.vehicleModel}
                    </Typography.Text>
                  </Center>
                </StackLayout>
                <StackLayout
                  direction="row"
                  alignItems="center"
                  divider={<Typography.Text color="gray">|</Typography.Text>}
                  gap={rpx(5)}
                >
                  <Typography.Text>
                    服务次数: {recordInfo.serviceCount}
                  </Typography.Text>
                  <Typography.Text>
                    {recordInfo.vehicleMileage}KM
                  </Typography.Text>
                  <Typography.Text>
                    业务员: {recordInfo.serviceAgentName}
                  </Typography.Text>
                </StackLayout>
              </StackLayout>
            </StackLayout>

            {/** check info */}
            <StackLayout gap={rpx(8)}>
              <Typography.Text>
                检验项目: {recordInfo.checkTitle}
              </Typography.Text>
              <Typography.Text>
                检验时间: {formatDateTime(recordInfo.checkTime ?? new Date())}
              </Typography.Text>
              <Typography.Text>
                检验人员: {recordInfo.checkAuthorName}
              </Typography.Text>
            </StackLayout>

            {/** rework record info */}
            <StackLayout gap={rpx(8)}>
              <StackLayout
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography.Text>
                  责任人员:{' '}
                  <Text
                    style={{
                      color: responsiblePersonName
                        ? '#000'
                        : StatusColors.Danger,
                    }}
                  >
                    {responsiblePersonName || '(未知)'}
                  </Text>
                </Typography.Text>
                <Center.Touchable onPress={onEditResponsiblePerson}>
                  <Typography.Text color="rgb(32, 127, 231)">
                    {responsiblePersonName ? '修改' : '选择责任人员'}
                  </Typography.Text>
                </Center.Touchable>
              </StackLayout>
              <StackLayout
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography.Text>车辆问题: </Typography.Text>
                <Typography.Text
                  color="rgb(154, 156, 158)"
                  light
                  size={rpx(12)}
                >
                  内返项目不对外展示仅用于门店记录
                </Typography.Text>
              </StackLayout>
            </StackLayout>

            <StackLayout gap={rpx(12)}>
              <ItemMediaList
                medias={medias}
                columns={4}
                editable={true}
                onTakeMedia={onTakeMedia}
                onDeleteMedia={onDeleteMedia}
                onPreviewVideo={onPreviewVideo}
                onRetryMediaUpload={onRetryMediaUpload}
              />

              <TextInput
                textAlignVertical="top"
                multiline
                placeholder="备注"
                placeholderTextColor={'#aaa'}
                clearButtonMode="always"
                value={remarks ?? ''}
                onChangeText={setRemarks}
                css={`
                  padding: ${rpx(8)}px ${rpx(10)}px;
                  line-height: ${rpx(22)}px;
                  font-size: ${rpx(14)}px;
                  height: ${rpx(60)}px;
                  border-color: #ddd;
                  border-width: 1px;
                  border-radius: ${rpx(10)}px;
                  font-family: ${FontFamily.NotoSans.Regular};
                `}
              />
            </StackLayout>
          </StackLayout>
        </ScrollView>
        {footerSlotName ? (
          <Fill name="rework-record-footer">{renderBottomAction()}</Fill>
        ) : (
          renderBottomAction()
        )}
      </>
    );
  },
  { title: '内返记录' },
);
